<script setup lang="ts">
/**
 * Interactive tutorials — the one mounted component, and the only thing that
 * knows what phase the tutorial is in.
 *
 * It owns the phase (welcome / run / done), which checkpoint the run is on, and
 * the sessionStorage that carries that across the hard reload between the Events
 * index and the event editor. TourOverlay renders a checkpoint; this decides
 * which one. mount.ts exposes the methods below as the public API for the
 * drawer (slice 6) and the auto-start gate (slice 7).
 *
 * NOTHING here starts on its own. A page load only ever RESUMES a run that was
 * already in progress; showing the welcome card is a call somebody makes.
 */

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import TourDone from './TourDone.vue'
import TourOverlay from './TourOverlay.vue'
import TourWelcome from './TourWelcome.vue'
import { firstNameFromDrawer } from './drawer'
import { MIN_TOUR_VIEWPORT, skipStepTarget, type TourExitReason } from './engine'
import { TOURS, type Tour } from './registry'
import {
  clearTourState,
  markExitToastPending,
  readTourState,
  takePendingExitToast,
  writeTourState,
  type TourPhase,
} from './state'
import { markTourCompleted, markWelcomeSeen } from './tutorialApi'

/** Ends the run wherever the florist is; the event they created stays. */
const EXIT_TOAST = 'Tutorial ended — restart any time from Tutorials in your profile menu.'
const TOAST_MS = 3200

/**
 * Where a run has to begin, and where the completion card's second button goes.
 *
 * Checkpoint 0's target is the New event button, which exists on the Events
 * index and nowhere else.
 */
const EVENTS_INDEX_PATH = '/events'

const phase = ref<TourPhase | null>(null)
const tourId = ref<string | null>(null)
const cpIndex = ref(0)
const firstName = ref<string | undefined>(undefined)
const toast = ref<string | null>(null)

const tour = computed<Tour | undefined>(() => TOURS.find((t) => t.id === tourId.value))
const viewportTooNarrow = ref(false)

let toastTimer = 0

function showToast(message: string): void {
  toast.value = message
  window.clearTimeout(toastTimer)
  toastTimer = window.setTimeout(() => {
    toast.value = null
  }, TOAST_MS)
}

/**
 * Only a RUN is persisted.
 *
 * The welcome and completion cards are deliberately in-memory: they are modal
 * over one page, and a florist who clicks a header link out of the welcome card
 * should land on Settings, not find the card following them there. A run is the
 * opposite — surviving the index → editor reload is the entire reason
 * sessionStorage is involved.
 */
function persist(): void {
  if (phase.value !== 'run' || !tourId.value) return
  writeTourState({ tourId: tourId.value, phase: 'run', cpIndex: cpIndex.value })
}

// ── public API (exposed through mount.ts) ─────────────────────────────────

function startTour(id: string): boolean {
  const found = TOURS.find((t) => t.id === id)
  if (!found) {
    console.warn(`[tour] no tutorial with id "${id}"`)
    return false
  }
  tourId.value = found.id
  cpIndex.value = 0
  phase.value = 'run'
  persist()
  return true
}

/**
 * Ends the run without recording a completion. Same for Exit and header nav —
 * only where the toast goes differs.
 *
 * `toastAfterReload` is for the header-link case: the browser is already
 * replacing this document, so a toast rendered now is destroyed before anyone
 * reads it. Hand it to sessionStorage and let the next boot show it.
 */
function exitTour(options: { toastAfterReload?: boolean } = {}): void {
  const wasRunning = phase.value === 'run'
  phase.value = null
  clearTourState()
  if (!wasRunning) return

  if (options.toastAfterReload) markExitToastPending()
  else showToast(EXIT_TOAST)
}

function onOverlayExit(reason: TourExitReason): void {
  exitTour({ toastAfterReload: reason === 'nav' })
}

function showWelcome(id: string, options: { firstName?: string } = {}): boolean {
  const found = TOURS.find((t) => t.id === id)
  if (!found) {
    console.warn(`[tour] no tutorial with id "${id}"`)
    return false
  }
  tourId.value = found.id
  firstName.value = options.firstName
  viewportTooNarrow.value = window.innerWidth < MIN_TOUR_VIEWPORT
  phase.value = 'welcome'
  return true
}

/**
 * Finish: record it server-side, drop the run state, show the completion card.
 * The server call is not awaited — the card is the florist's confirmation, and
 * a slow or failed request must not hold it back (tutorialApi.ts never throws).
 */
function completeTour(): void {
  const id = tourId.value
  if (!id) return
  clearTourState()
  phase.value = 'done'
  void markTourCompleted(id)
}

/** For the console and for tests: what the engine currently thinks it is doing. */
function currentState(): { phase: TourPhase | null; tourId: string | null; cpIndex: number } {
  return { phase: phase.value, tourId: tourId.value, cpIndex: cpIndex.value }
}

/**
 * Show the completion card without having run the tour — slice 7 needs it to
 * re-open the card, and it is how the design is checked by hand. Separate from
 * completeTour, which also records the completion server-side.
 */
function showDone(id: string): boolean {
  const found = TOURS.find((t) => t.id === id)
  if (!found) {
    console.warn(`[tour] no tutorial with id "${id}"`)
    return false
  }
  tourId.value = found.id
  phase.value = 'done'
  return true
}

defineExpose({ startTour, exitTour, showWelcome, showDone, completeTour, currentState })

// ── run mechanics ─────────────────────────────────────────────────────────

function advance(): void {
  const checkpoints = tour.value?.checkpoints
  if (!checkpoints) return
  const next = cpIndex.value + 1
  if (next >= checkpoints.length) {
    completeTour()
    return
  }
  cpIndex.value = next
  persist()
}

/** The florist acted ahead; land on `index`, or complete if that is the end. */
function jumpTo(index: number): void {
  const checkpoints = tour.value?.checkpoints
  if (!checkpoints) return
  if (index >= checkpoints.length) {
    completeTour()
    return
  }
  cpIndex.value = Math.max(0, index)
  persist()
}

/** One checkpoint back (the card only offers it where the overlay allows). */
function back(): void {
  if (cpIndex.value <= 0) return
  cpIndex.value -= 1
  persist()
}

function skipStep(): void {
  const checkpoints = tour.value?.checkpoints
  if (!checkpoints) return
  const target = skipStepTarget(checkpoints, cpIndex.value)
  if (target.kind === 'complete') {
    completeTour()
    return
  }
  cpIndex.value = target.index
  persist()
}

/**
 * True when this page is the Events index, where a run must begin.
 *
 * The trailing-slash form is accepted because a link elsewhere in the app or a
 * browser autocomplete can produce it and it is the same screen; anything
 * deeper (`/events/wilson-tran`) is the editor, which is not.
 */
function onEventsIndex(): boolean {
  const path = window.location.pathname
  return path === EVENTS_INDEX_PATH || path === `${EVENTS_INDEX_PATH}/`
}

/**
 * The card has been answered: tell the server, and disarm THIS document too.
 *
 * The server write alone is not enough, and a browser proves it. Turbo caches
 * the Events index's <body> on the way out and renders that snapshot back on a
 * browser Back — a snapshot taken while `data-tour-autostart` was still in the
 * markup, because the server's answer changed and this document did not. The
 * engine remounts on the restored body, reads the stale attribute and offers the
 * card again, with its full-screen scrim over the nav.
 *
 * Measured in Chrome before this line existed: skip the card, click one nav
 * link, press Back — `tutorial_welcome_seen` is already true server-side and the
 * card is on screen anyway. Removing the attribute now means it is absent from
 * the snapshot Turbo takes when the visit starts.
 *
 * querySelectorAll, not the single element autostartTourId() reads: this is a
 * cleanup, and leaving a second copy behind would leave the bug behind with it.
 */
function answerWelcomeCard(): void {
  void markWelcomeSeen()
  document
    .querySelectorAll('[data-tour-autostart]')
    .forEach((el) => el.removeAttribute('data-tour-autostart'))
}

function onWelcomeStart(): void {
  // Seen either way: the gate that offers this card asks whether it has been
  // shown, not whether the tour was finished. A florist who starts and wanders
  // off has still seen it, and can restart from the Learn group.
  answerWelcomeCard()
  const id = tourId.value
  if (!id) return

  if (onEventsIndex()) {
    startTour(id)
    return
  }

  // Raised from the Learn group in the profile drawer (slice 6), which is on
  // every signed-in page. Starting the overlay here would spotlight a button
  // that is on another screen, so persist the run at checkpoint 0 and go to the
  // index — the next boot reads this back and resumes. Writing the state before
  // navigating, not after arriving, is the same reason ne-save advances
  // immediately: nothing of this document survives the trip except
  // sessionStorage.
  writeTourState({ tourId: id, phase: 'run', cpIndex: 0 })
  window.location.assign(EVENTS_INDEX_PATH)
}

/**
 * "Not now" — for good, and for whatever was running underneath.
 *
 * The card can be raised over a live run: Redo from the Learn group mid-tour
 * leaves the previous run's checkpoint in sessionStorage while the card sits on
 * top of it. Dropping the card without dropping that state would leave the run
 * orphaned — nothing is on screen, and the next page load quietly resumes a tour
 * the florist just declined. Skip means no tutorial, so it ends the displaced
 * run too. Skipping with nothing stored is what clearTourState() already
 * handles: a removeItem of a key that isn't there.
 */
function onWelcomeSkip(): void {
  answerWelcomeCard()
  clearTourState()
  phase.value = null
}

function onDoneClose(): void {
  phase.value = null
}

function onDoneEvents(): void {
  phase.value = null
  window.location.href = EVENTS_INDEX_PATH
}

// ── boot ──────────────────────────────────────────────────────────────────

/**
 * Pick a run left in sessionStorage back up. True if one is now running.
 *
 * This is the index → editor hop and every ordinary reload during a tour; state
 * that is not a runnable run is dropped rather than carried.
 */
function resumeStoredRun(): boolean {
  const stored = readTourState()
  if (!stored) return false

  const found = TOURS.find((t) => t.id === stored.tourId)
  if (!found || stored.phase !== 'run') {
    clearTourState()
    return false
  }
  // A stored index past the end means the script shrank under a running tour
  // (a deploy mid-run). Resuming on the last checkpoint is recoverable; an
  // undefined checkpoint is a blank card that can never advance.
  tourId.value = found.id
  cpIndex.value = Math.min(stored.cpIndex, found.checkpoints.length - 1)
  phase.value = 'run'
  return true
}

/**
 * The tutorial this page is offering unprompted, or null.
 *
 * The server decides — EventsController#index's gate, rendered as
 * `data-tour-autostart` on the Events index (docs/INTERACTIVE_TUTORIALS_PLAN.md
 * §3.3). It is a question about rows the server already has in front of it —
 * the welcome card not yet seen, the studio set up, nothing real quoted — so
 * re-deriving it here would mean shipping them to the browser to be recomputed.
 * The attribute is simply absent when the gate is closed.
 *
 * Queried across the document rather than off <body>: which element carries it
 * is the view's business, and only the Events index carries it at all.
 */
function autostartTourId(): string | null {
  const el = document.querySelector<HTMLElement>('[data-tour-autostart]')
  return el?.dataset.tourAutostart?.trim() || null
}

onMounted(() => {
  // The toast a header link never got to show. Read-and-clear, so it appears on
  // the page the florist actually landed on and on no page after that.
  const justExited = takePendingExitToast()
  if (justExited) showToast(EXIT_TOAST)

  // Resuming comes first, and a resumed run means no card. Start pressed in the
  // drawer somewhere other than the index writes the run and navigates HERE, so
  // the arriving page is an armed Events index with a run pending: offering the
  // welcome card again would put it on top of the tour it just began.
  if (resumeStoredRun()) return

  // Ending a run by clicking a header link lands on the very page that arms the
  // gate, and the "I've seen it" write is still in flight. Re-offering the
  // tutorial in the same breath as the toast saying it ended is not an offer,
  // it is a loop.
  if (justExited) return

  const id = autostartTourId()
  if (!id) return

  // Narrow viewports are not turned away here — showWelcome renders the card's
  // "open BLUME on a laptop" variant, which is the README's rule (§1) and the
  // only way a florist on a tablet learns the tour exists at all.
  showWelcome(id, { firstName: firstNameFromDrawer() })
})

onBeforeUnmount(() => window.clearTimeout(toastTimer))
</script>

<template>
  <div class="tour-root">
    <TourWelcome
      v-if="phase === 'welcome' && tour"
      :tour="tour"
      :first-name="firstName"
      :viewport-too-narrow="viewportTooNarrow"
      @start="onWelcomeStart"
      @skip="onWelcomeSkip"
    />

    <TourOverlay
      v-else-if="phase === 'run' && tour"
      :key="tour.id"
      :checkpoints="tour.checkpoints"
      :steps="tour.steps"
      :index="cpIndex"
      @advance="advance"
      @skip-step="skipStep"
      @back="back"
      @jump="jumpTo"
      @exit="onOverlayExit"
    />

    <TourDone
      v-else-if="phase === 'done' && tour"
      :tour="tour"
      @close="onDoneClose"
      @events="onDoneEvents"
    />

    <div v-if="toast" class="tour-toast" role="status">{{ toast }}</div>
  </div>
</template>
